import { useId, useState } from 'react';
import { CheckCircle2, Info, MessageCircleQuestion, Send, XCircle } from 'lucide-react';
import { Modal } from '../../components/Modal/Modal';
import { formatDateTime } from '../../lib/format';
import type { Campaign } from '../../lib/supabase';
import '../Assistent/MailAssistent.css';

export type Entscheidung = 'freigeben' | 'versand' | 'rueckfrage' | 'ablehnen';

type Props = {
  art: Entscheidung;
  c: Campaign;
  laeuft: boolean;
  onAbbrechen: () => void;
  /** grund bei Rückfrage/Ablehnen, zeitpunkt (ISO) beim Freigeben — null = sofort */
  onBestaetigen: (werte: { grund: string | null; zeitpunkt: string | null }) => void;
};

const VORLAUF_MIN = 10;

function lokal(d: Date): string {
  const z = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
}

/**
 * Freigeben, nachfragen, ablehnen — mit beschriftetem Feld statt
 * window.prompt, wie im Event Manager.
 */
export function EntscheidenDialog({ art, c, laeuft, onAbbrechen, onBestaetigen }: Props) {
  const feldId = useId();
  const person = c.created_by_name ?? 'Die Person';
  const wunsch = c.desired_send_at ? new Date(c.desired_send_at) : null;
  const wunschGeht = !!wunsch && wunsch.getTime() > Date.now() + VORLAUF_MIN * 60_000;
  const [wann, setWann] = useState<'wunsch' | 'sofort' | 'anders'>(wunschGeht ? 'wunsch' : 'sofort');
  const [anders, setAnders] = useState(() => lokal(new Date(Date.now() + 60 * 60_000)));
  const [grund, setGrund] = useState('');

  const planen = art === 'freigeben' || art === 'versand';
  const andersZeit = new Date(anders);
  const andersOk = !Number.isNaN(andersZeit.getTime()) && andersZeit.getTime() > Date.now() + VORLAUF_MIN * 60_000;
  const bereit = planen ? (wann !== 'anders' || andersOk) : grund.trim().length > 0;

  const titel = {
    freigeben: 'Mail freigeben',
    versand: 'Versand anstoßen',
    rueckfrage: 'Rückfrage stellen',
    ablehnen: 'Mail ablehnen',
  }[art];

  const absenden = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bereit || laeuft) return;
    if (planen) {
      const zeitpunkt = wann === 'wunsch' && wunsch ? wunsch.toISOString() : wann === 'anders' ? andersZeit.toISOString() : null;
      onBestaetigen({ grund: null, zeitpunkt });
    } else {
      onBestaetigen({ grund: grund.trim(), zeitpunkt: null });
    }
  };

  return (
    <Modal isOpen onClose={onAbbrechen} title={titel} size="md">
      <form onSubmit={absenden} className="fg-dialog">
        <p className="fg-dialog-betreff">„{c.original_subject || c.subject}"</p>

        {planen && (
          <>
            <fieldset className="fg-wann">
              <legend className="form-label">Wann soll die Mail rausgehen?</legend>
              <label className={`as-option${wann === 'wunsch' ? ' aktiv' : ''}${!wunschGeht ? ' fg-aus' : ''}`}>
                <input type="radio" name="wann" checked={wann === 'wunsch'} disabled={!wunschGeht} onChange={() => setWann('wunsch')} />
                <span>Zum Wunschtermin{wunsch ? ` (${formatDateTime(c.desired_send_at)})` : ''}{wunsch && !wunschGeht ? ' — schon vorbei' : ''}</span>
              </label>
              <label className={`as-option${wann === 'sofort' ? ' aktiv' : ''}`}>
                <input type="radio" name="wann" checked={wann === 'sofort'} onChange={() => setWann('sofort')} />
                <span>Jetzt sofort</span>
              </label>
              <label className={`as-option${wann === 'anders' ? ' aktiv' : ''}`}>
                <input type="radio" name="wann" checked={wann === 'anders'} onChange={() => setWann('anders')} />
                <span>Anderer Termin</span>
              </label>
              {wann === 'anders' && (
                <input type="datetime-local" className={`form-input${andersOk ? '' : ' input-error'}`} value={anders} onChange={(e) => setAnders(e.target.value)} aria-label="Anderer Termin" />
              )}
              {wann === 'anders' && !andersOk && <p className="field-error">Bitte einen Termin mindestens {VORLAUF_MIN} Minuten in der Zukunft wählen.</p>}
            </fieldset>
            <div className="alert alert-info">
              <Info size={16} aria-hidden="true" />
              <div>
                {art === 'freigeben'
                  ? <>Mit der Freigabe entsteht der Brevo-Entwurf samt internen Verteilern, und die Mail wird eingeplant. {person} bekommt eine Nachricht.</>
                  : <>Die Mail ist schon freigegeben, der Versand wurde aber nicht eingeplant. Hier stößt Du ihn erneut an.</>}
              </div>
            </div>
          </>
        )}

        {!planen && (
          <div className="form-group">
            <label className="form-label" htmlFor={feldId}>
              {art === 'rueckfrage' ? 'Was muss geklärt oder geändert werden?' : 'Warum lehnst Du die Mail ab?'}
            </label>
            <textarea id={feldId} className="form-textarea" rows={4} value={grund} onChange={(e) => setGrund(e.target.value)} autoFocus
              placeholder={art === 'rueckfrage' ? 'z. B. Bitte die Uhrzeit am Freitag prüfen — im Stundenplan steht 17:30.' : 'z. B. Das Thema kommt schon im Newsletter am Montag.'} />
            <p className="form-hint">
              {art === 'rueckfrage'
                ? `Die Mail geht als Entwurf zurück an ${person} — mit Deiner Frage. Danach reicht ${person} sie erneut ein.`
                : `Die Mail wird abgelegt und nicht verschickt. ${person} bekommt Deine Begründung per Mail.`}
            </p>
          </div>
        )}

        <div className="fg-dialog-fuss">
          <button type="button" className="btn btn-ghost" onClick={onAbbrechen} disabled={laeuft}>Abbrechen</button>
          <button type="submit" className={`btn ${art === 'ablehnen' ? 'btn-danger' : 'btn-primary'}`} disabled={!bereit || laeuft}>
            {art === 'freigeben' && <><CheckCircle2 size={16} aria-hidden="true" /> {laeuft ? 'Wird freigegeben …' : 'Freigeben'}</>}
            {art === 'versand' && <><Send size={16} aria-hidden="true" /> {laeuft ? 'Wird eingeplant …' : 'Versand anstoßen'}</>}
            {art === 'rueckfrage' && <><MessageCircleQuestion size={16} aria-hidden="true" /> {laeuft ? 'Wird gesendet …' : 'Rückfrage senden'}</>}
            {art === 'ablehnen' && <><XCircle size={16} aria-hidden="true" /> {laeuft ? 'Wird abgelehnt …' : 'Ablehnen'}</>}
          </button>
        </div>
      </form>
    </Modal>
  );
}
